import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {BehaviorSubject, catchError, map, Observable, of} from "rxjs";
import {Tag} from "../model/tag";
import {Melody} from "../model/melody";
import {Artist} from "../model/artist";
import {constants} from "../constants";
import {updateArtistPaths, updateArtistsPaths, updateMelodiesPaths, updateMelodyPaths} from "../utils/paths-utils";
import {MainPageBanner} from "../model/main-page-banner";

@Injectable({
  providedIn: 'root'
})
export class ContentService {
  private readonly _tagsList = new BehaviorSubject<ReadonlyArray<Tag>>([]);
  private readonly _hitsMelodies = new BehaviorSubject<ReadonlyArray<Melody>>([]);
  private readonly _mainPageBanners = new BehaviorSubject<ReadonlyArray<MainPageBanner>>([]);
  private readonly _mainPageSpecialBanners = new BehaviorSubject<ReadonlyArray<MainPageBanner>>([]);

  constructor(private readonly httpClient: HttpClient) {
    this.loadTags();
    this.loadHitMelodies();
    this.loadMainPageBanners();
    this.loadMainPageSpecialBanners()
  }

  get tagsList(): Observable<ReadonlyArray<Tag>> {
    return this._tagsList.asObservable();
  }

  get mainPageBanners(): Observable<ReadonlyArray<MainPageBanner>> {
    return this._mainPageBanners.asObservable();
  }

  get mainPageSpecialBanners(): Observable<ReadonlyArray<MainPageBanner>> {
    return this._mainPageSpecialBanners.asObservable();
  }

  get hitsMelodies(): Observable<ReadonlyArray<Melody>> {
    return this._hitsMelodies.asObservable();
  }

  private loadTags(): void {
    this.httpClient.get<ReadonlyArray<Tag>>('/content/tags')
      .pipe(catchError(() => {
        return of(new Array<Tag>())
      })).subscribe(tags => this._tagsList.next(tags));
  }

  private loadMainPageBanners() {
    this.httpClient.get<ReadonlyArray<MainPageBanner>>('/content/banners')
      .pipe(catchError(() => {
        return of(new Array<MainPageBanner>())
      })).subscribe(banners => {
        this._mainPageBanners.next(banners)
    });
  }

  getTag(id: number): Observable<Tag> {
    return this.httpClient.get<Tag>(`/content/tags/${id}`)
      .pipe(catchError(() => {
        return of(null)
      }));
  }

  getTagMelodies(id: number, page: number, size: number, sort?: string, asc?: boolean): Observable<ReadonlyArray<Melody>> {
    let params = new HttpParams()
      .append('page', page)
      .append('size', size);
    if (sort) {
      params = params.append('sort', sort);
    }
    if (asc) {
      params = params.append('asc', asc);
    }

    return this.getMelodiesList(`/content/tags/${id}/melodies`, params);
  }

  getTagMelodiesCount(id: number): Observable<number> {
    return this.httpClient.get<number>(`/content/tags/${id}/melodies/count`)
      .pipe(
        catchError(() => {
          return of(0)
        }));
  }

  getRandomTagMelodies(id: number, size: number): Observable<ReadonlyArray<Melody>> {
    return this.getMelodiesList(`/content/tags/${id}/melodies/random`, {
      size: size
    });
  }

  getArtist(id: number): Observable<Artist> {
    return this.httpClient.get<Artist>(`/content/artists/${id}`)
      .pipe(
        map(updateArtistPaths),
        catchError(() => {
          return of(null)
        }));
  }

  getArtistMelodies(id: number, page: number, size: number): Observable<ReadonlyArray<Melody>> {
    return this.getMelodiesList(`/content/artists/${id}/melodies`, {
      page: page,
      size: size
    });
  }

  getArtistMelodiesCount(id: number): Observable<number> {
    return this.httpClient.get<number>(`/content/artists/${id}/melodies/count`)
      .pipe(
        catchError(() => {
          return of(0)
        }));
  }

  getAllMelodies(page: number, size: number, sort?: string, asc?: boolean): Observable<ReadonlyArray<Melody>> {
    let params = new HttpParams()
      .append('page', page)
      .append('size', size);
    if (sort) {
      params = params.append('sort', sort);
    }
    if (asc) {
      params = params.append('asc', asc);
    }

    return this.getMelodiesList(`/content/melodies`, params);
  }

  getAllMelodiesCount(): Observable<number> {
    return this.httpClient.get<number>(`/content/melodies/count`)
      .pipe(
        catchError(() => {
          return of(0)
        }));
  }

  getMelodyById(id: number): Observable<Melody> {
    return this.httpClient.get<Melody>(`/content/melodies/${id}`)
      .pipe(
        map(updateMelodyPaths),
        catchError(() => {
          return of(null)
        }));
  }

  getRecommendedMelodies(melodyId: number, size: number): Observable<ReadonlyArray<Melody>> {
    return this.getMelodiesList(`/content/melodies/${melodyId}/recommended`, {
      size: size
    });
  }

  getBannerMelodies(id: number): Observable<ReadonlyArray<Melody>> {
    return this.getMelodiesList('/content/melodies/banner', {
      id: id
    });
  }

  searchMelodies(text: string, page: number, size: number): Observable<ReadonlyArray<Melody>> {
    return this.getMelodiesList('/content/melodies/search', {
      text: text,
      page: page,
      size: size
    });
  }


  searchMelodiesCount(text: string): Observable<number> {
    return this.searchCount('/content/melodies/search/count', text);
  }

  searchArtists(text: string, page: number, size: number): Observable<ReadonlyArray<Artist>> {
    return this.getArtistsList('/content/artists/search', {
      text: text,
      page: page,
      size: size
    });
  }

  searchArtistsCount(text: string): Observable<number> {
    return this.searchCount('/content/artists/search/count', text);
  }

  getArtistsList(url: string, params?: { [param: string]: string | number | boolean }): Observable<ReadonlyArray<Artist>> {
    return this.httpClient.get<ReadonlyArray<Artist>>(url, {
      params: params
    })
      .pipe(
        map(updateArtistsPaths),
        catchError(() => {
          return of(new Array<Artist>())
        }));
  }

  getMelodiesList(url: string, params?: HttpParams | { [param: string]: string | number | boolean }): Observable<ReadonlyArray<Melody>> {
    return this.httpClient.get<ReadonlyArray<Melody>>(url, {
      params: params
    })
      .pipe(
        map(updateMelodiesPaths),
        catchError(() => {
          return of(new Array<Melody>())
        }));
  }

  searchCount(url: string, text: string): Observable<number> {
    return this.httpClient.get<number>(url, {
      params: {
        text: text
      }
    })
      .pipe(
        catchError(() => {
          return of(0)
        }));
  }

  private loadHitMelodies(): void {
    this.getBannerMelodies(constants.hitMelodiesId)
      .subscribe(melodies => this._hitsMelodies.next(melodies));
  }

  private loadMainPageSpecialBanners() {
    this.httpClient.get<ReadonlyArray<MainPageBanner>>('/content/banners/special')
      .pipe(catchError(() => {
        return of(new Array<MainPageBanner>())
      })).subscribe(banners => {
      this._mainPageSpecialBanners.next(banners)
    });
  }
}
